import { DateTime } from "luxon";
import { z } from "zod";

export const REASON_CODES = [
  "CONTRACT_CHANGE",
  "APPROVED_EXCEPTION",
  "PRICING_UPDATE",
  "TEMPORARY_CHANGE",
  "CORRECTION",
  "PRODUCT_CHANGE",
  "OTHER",
] as const;

// Sentinel value used in the bundle dropdown to indicate the user wants to
// create a fresh single-use bundle from per-SKU prices instead of selecting
// an existing bundle. The backend swaps this for the real bundle id after
// creating the new PricingBundle row.
export const CUSTOM_PRICING_BUNDLE_ID = "__custom__";

const MIN_PRICE_IN_MILLICENTS = 10;
const DOLLAR_IN_MILLICENTS = 100 * 1000; // cents * millicents-per-cent

// Per-SKU custom-pricing cap: standard bundle price plus this offset.
export const CUSTOM_PRICE_MAX_OFFSET_MILLICENTS = 250;

export type StandardPricesBySkuId = Record<string, number>;

function formatMillicentsAsDollars(millicents: number): string {
  return `$${millicents / DOLLAR_IN_MILLICENTS}`;
}

function maxPriceForSku(
  skuId: string,
  standardPricesBySkuId: StandardPricesBySkuId
): number | undefined {
  const standard = standardPricesBySkuId[skuId];
  return standard === undefined
    ? undefined
    : standard + CUSTOM_PRICE_MAX_OFFSET_MILLICENTS;
}

const priceInMillicentsSchema = z
  .number({
    required_error: "Required",
    invalid_type_error: "Required",
  })
  .int("Must be a whole number of millicents")
  .min(
    MIN_PRICE_IN_MILLICENTS,
    `Must be at least ${MIN_PRICE_IN_MILLICENTS} millicents (${(
      MIN_PRICE_IN_MILLICENTS / 1000
    ).toFixed(2)} cents)`
  );

// Shared per-SKU max-cap check: the single source of truth for the
// "price must not exceed the Standard price + offset" rule, used by both the
// custom-pricing schemas and the new-bundle schema.
function refineMaxPrice(
  ctx: z.RefinementCtx,
  skuId: string,
  priceInMillicents: number,
  standardPricesBySkuId: StandardPricesBySkuId,
  path: (string | number)[] = ["priceInMillicents"]
): void {
  const max = maxPriceForSku(skuId, standardPricesBySkuId);
  if (max !== undefined && priceInMillicents > max) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Must be at most ${formatMillicentsAsDollars(max)}`,
      path,
    });
  }
}

function makeCustomPriceSchema(standardPricesBySkuId: StandardPricesBySkuId) {
  return z
    .object({
      skuId: z.string().min(1),
      priceInMillicents: priceInMillicentsSchema,
    })
    .superRefine((entry, ctx) => {
      refineMaxPrice(
        ctx,
        entry.skuId,
        entry.priceInMillicents,
        standardPricesBySkuId
      );
    });
}

function makeCustomPriceListField(
  standardPricesBySkuId: StandardPricesBySkuId
) {
  return z.array(makeCustomPriceSchema(standardPricesBySkuId)).nullish();
}

export type ReasonCodeValue = (typeof REASON_CODES)[number];

export const reasonCodeSchema = z.enum(REASON_CODES, {
  errorMap: (issue, ctx) => {
    if (
      issue.code === "invalid_type" &&
      (ctx.data === undefined || ctx.data === null)
    ) {
      return { message: "Required" };
    }
    return { message: `Invalid reason code: ${String(ctx.data)}` };
  },
});

export const PRICING_BOUNDARY_TIMEZONE = "America/Los_Angeles";

function toPricingBoundary(value: Date): Date {
  return DateTime.fromJSDate(value)
    .setZone(PRICING_BOUNDARY_TIMEZONE)
    .startOf("day")
    .toJSDate();
}

// Accepts a JS Date or a valid Luxon DateTime; outputs a JS Date normalized
// to the start of the day (any time component is stripped) of the pricing timezone.
const flexDateSchema = z
  .union(
    [
      z.date({ errorMap: () => ({ message: "Required" }) }),
      z
        .custom<DateTime>(
          (val): val is DateTime => val instanceof DateTime && val.isValid
        )
        .transform(dt => dt.toJSDate()),
    ],
    {
      errorMap: () => ({ message: "Required" }),
    }
  )
  .transform(toPricingBoundary);

const nullableFlexDateSchema = flexDateSchema.nullable();

// Preprocess a reason-code field: "" (MUI Select default) becomes null so that
// a union-abort on the empty string does not prevent superRefine from running.
const nullableReasonCodeField = z.preprocess(
  (val): unknown => (val === "" ? null : val),
  reasonCodeSchema.nullable()
);

function makeBasePricingAssignmentFields(
  standardPricesBySkuId: StandardPricesBySkuId
) {
  return {
    initialBundleId: z.string().min(1, "Required"),
    // Use nullable so that an empty string (preprocessed to null) passes base
    // validation and does not abort superRefine.
    initialReasonCode: nullableReasonCodeField,
    initialNotes: z.string().nullish(),
    // Accept null so the form's default null value does not abort superRefine.
    effectiveDate: nullableFlexDateSchema,
    expirationDate: nullableFlexDateSchema.optional(),
    followUpBundleId: z.string().nullish(),
    followUpReasonCode: z.preprocess(
      (val): unknown => (val === "" ? null : val),
      reasonCodeSchema.nullable().optional()
    ),
    followUpNotes: z.string().nullish(),
    initialCustomPrices: makeCustomPriceListField(standardPricesBySkuId),
    followUpCustomPrices: makeCustomPriceListField(standardPricesBySkuId),
  };
}

type CustomPriceValue = { skuId: string; priceInMillicents: number };
type CustomPriceField = CustomPriceValue[] | null | undefined;

function isCustomPricesEmpty(value: CustomPriceField): boolean {
  return value == null || value.length === 0;
}

type PricingAssignmentData = {
  initialBundleId: string;
  initialReasonCode: ReasonCodeValue | null;
  initialNotes?: string | null;
  effectiveDate: Date | null;
  expirationDate?: Date | null;
  followUpBundleId?: string | null;
  followUpReasonCode?: ReasonCodeValue | null;
  followUpNotes?: string | null;
  initialCustomPrices?: CustomPriceField;
  followUpCustomPrices?: CustomPriceField;
  customIsWeText?: boolean | null;
};

function refinePricingAssignment(
  data: PricingAssignmentData,
  ctx: z.RefinementCtx,
  {
    requireFollowUpBundle,
    requireCustomIsWeText,
  }: {
    requireFollowUpBundle: boolean;
    requireCustomIsWeText: boolean;
  }
): void {
  if (!data.initialReasonCode) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Required",
      path: ["initialReasonCode"],
    });
  }
  if (!data.effectiveDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Required",
      path: ["effectiveDate"],
    });
  }
  if (data.initialReasonCode === "OTHER" && !data.initialNotes?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Notes are required when the change reason is Other",
      path: ["initialNotes"],
    });
  }

  const initialIsCustom = data.initialBundleId === CUSTOM_PRICING_BUNDLE_ID;
  if (initialIsCustom) {
    if (isCustomPricesEmpty(data.initialCustomPrices)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["initialCustomPrices"],
      });
    }
    if (
      requireCustomIsWeText &&
      (data.customIsWeText === null || data.customIsWeText === undefined)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["customIsWeText"],
      });
    }
  }

  if (data.expirationDate) {
    if (data.effectiveDate) {
      const oneDayAfter = DateTime.fromJSDate(data.effectiveDate)
        .setZone(PRICING_BOUNDARY_TIMEZONE)
        .plus({ days: 1 })
        .startOf("day")
        .toJSDate();
      if (data.expirationDate < oneDayAfter) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Expiration date must be at least one day after the effective date",
          path: ["expirationDate"],
        });
      }
    }
    // followUpReasonCode/followUpNotes without a followUpBundleId can occur when
    // the form held stale values that became hidden (e.g. followUpBundleId
    // switched to "None" after a reason was already chosen). Treat the bundle
    // as required whenever any follow-up field carries data.
    const hasFollowUpReasonOrNotes =
      !!data.followUpReasonCode || !!data.followUpNotes?.trim();
    if (
      (requireFollowUpBundle || hasFollowUpReasonOrNotes) &&
      !data.followUpBundleId
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["followUpBundleId"],
      });
    } else if (
      data.followUpBundleId &&
      data.followUpBundleId === data.initialBundleId &&
      data.followUpBundleId !== CUSTOM_PRICING_BUNDLE_ID
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "After-expiration bundle must be different from the primary bundle",
        path: ["followUpBundleId"],
      });
    }
    if (data.followUpBundleId === CUSTOM_PRICING_BUNDLE_ID) {
      if (isCustomPricesEmpty(data.followUpCustomPrices)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["followUpCustomPrices"],
        });
      }
    }
    if (data.followUpBundleId && !data.followUpReasonCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["followUpReasonCode"],
      });
    } else if (requireFollowUpBundle && !data.followUpReasonCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["followUpReasonCode"],
      });
    }
    if (data.followUpReasonCode === "OTHER" && !data.followUpNotes?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Notes are required when the change reason is Other",
        path: ["followUpNotes"],
      });
    }
  }
}

// Per-SKU price for a brand-new reusable bundle. Reuses the same min/integer
// (`priceInMillicentsSchema`) and max-cap (`refineMaxPrice`) rules as custom
// prices; the only difference is the price is nullable: a blank input (or an
// explicit null from the mutation) means "fall back to this SKU's rack price".
// Blank/NaN/undefined coming from the RHF currency field are preprocessed to
// null so the optional price still validates instead of erroring with
// "Required", and the cap check is skipped for the null (rack-fallback) case.
function makeNewBundlePriceSchema(
  standardPricesBySkuId: StandardPricesBySkuId
) {
  return z
    .object({
      skuId: z.string().min(1, "Required"),
      priceInMillicents: z.preprocess(
        (val): unknown =>
          val === "" ||
          val === null ||
          val === undefined ||
          (typeof val === "number" && Number.isNaN(val))
            ? null
            : val,
        priceInMillicentsSchema.nullable()
      ),
    })
    .superRefine((entry, ctx) => {
      if (entry.priceInMillicents === null) {
        return;
      }
      refineMaxPrice(
        ctx,
        entry.skuId,
        entry.priceInMillicents,
        standardPricesBySkuId
      );
    });
}

// Cap-less variant (no Standard prices) — retained for the input type export
// and any caller that validates without the per-SKU max.
export const createNewPricingBundleSchema = z.object({
  name: z
    .string()
    .transform(s => s.trim())
    .refine(s => s.length > 0, "Bundle name cannot be empty or whitespace"),
  isWeText: z.boolean(),
  prices: z
    .array(makeNewBundlePriceSchema({}))
    .min(1, "At least one price must be provided"),
});

export type CreateNewPricingBundleInput = z.input<
  typeof createNewPricingBundleSchema
>;

export function makeCreateNewPricingBundleSchema({
  standardPricesBySkuId,
}: {
  standardPricesBySkuId: StandardPricesBySkuId;
}): typeof createNewPricingBundleSchema {
  return z.object({
    name: z
      .string()
      .transform(s => s.trim())
      .refine(s => s.length > 0, "Bundle name cannot be empty or whitespace"),
    isWeText: z.boolean(),
    prices: z
      .array(makeNewBundlePriceSchema(standardPricesBySkuId))
      .min(1, "At least one price must be provided"),
  });
}

export const changeTenantPricingAssignmentSchema = z
  .object({
    ...makeBasePricingAssignmentFields({}),
    customIsWeText: z.boolean().nullish(),
  })
  .superRefine((data, ctx) => {
    refinePricingAssignment(data, ctx, {
      requireFollowUpBundle: true,
      requireCustomIsWeText: true,
    });
  });

const _bulkChangeTenantsPricingAssignmentSchemaSample = z
  .object({
    ...makeBasePricingAssignmentFields({}),
    tenantIds: z.array(z.string().min(1)).min(1, "Required"),
    customIsWeText: z.boolean().nullish(),
  })
  .superRefine((data, ctx) => {
    refinePricingAssignment(data, ctx, {
      requireFollowUpBundle: true,
      requireCustomIsWeText: true,
    });
  });

const _createSubaccountPricingAssignmentsSchemaSample = z
  .object({
    ...makeBasePricingAssignmentFields({}),
    subaccountIds: z.array(z.string().min(1)).min(1, "Required"),
  })
  .superRefine((data, ctx) => {
    refinePricingAssignment(data, ctx, {
      requireFollowUpBundle: false,
      requireCustomIsWeText: false,
    });
  });

export function makeChangeTenantPricingAssignmentSchema({
  standardPricesBySkuId,
}: {
  standardPricesBySkuId: StandardPricesBySkuId;
}): typeof changeTenantPricingAssignmentSchema {
  return z
    .object({
      ...makeBasePricingAssignmentFields(standardPricesBySkuId),
      customIsWeText: z.boolean().nullish(),
    })
    .superRefine((data, ctx) => {
      refinePricingAssignment(data, ctx, {
        requireFollowUpBundle: true,
        requireCustomIsWeText: true,
      });
    });
}

export function makeBulkChangeTenantsPricingAssignmentSchema({
  standardPricesBySkuId,
}: {
  standardPricesBySkuId: StandardPricesBySkuId;
}): typeof _bulkChangeTenantsPricingAssignmentSchemaSample {
  return z
    .object({
      ...makeBasePricingAssignmentFields(standardPricesBySkuId),
      tenantIds: z.array(z.string().min(1)).min(1, "Required"),
      customIsWeText: z.boolean().nullish(),
    })
    .superRefine((data, ctx) => {
      refinePricingAssignment(data, ctx, {
        requireFollowUpBundle: true,
        requireCustomIsWeText: true,
      });
    });
}

export function makeCreateSubaccountPricingAssignmentsSchema({
  standardPricesBySkuId,
}: {
  standardPricesBySkuId: StandardPricesBySkuId;
}): typeof _createSubaccountPricingAssignmentsSchemaSample {
  return z
    .object({
      ...makeBasePricingAssignmentFields(standardPricesBySkuId),
      subaccountIds: z.array(z.string().min(1)).min(1, "Required"),
    })
    .superRefine((data, ctx) => {
      refinePricingAssignment(data, ctx, {
        requireFollowUpBundle: false,
        requireCustomIsWeText: false,
      });
    });
}
